import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue'

import { insightApi } from '../api/InsightApi'
import type { InsightResult } from '../models/insight.types'
import { useInsightStore, type InsightStore } from '../stores/insightStore'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError, type Paginated } from '@/shared/http'
import type { UiAsyncState } from '@/shared/ui'

interface PanelApiPort {
  list: (
    accountId: number,
    workspaceId: number,
    params: { limit: number; offset: number },
    signal?: AbortSignal,
  ) => Promise<Paginated<InsightResult>>
}

export interface UseReportInsightsPanelOptions {
  accountId?: MaybeRefOrGetter<number | undefined>
  workspaceId?: MaybeRefOrGetter<string | number | undefined>
  api?: PanelApiPort
  store?: InsightStore
  limit?: number
  cacheKey?: string
}

export interface UseReportInsightsPanel {
  insights: ComputedRef<readonly InsightResult[]>
  count: ComputedRef<number>
  state: ComputedRef<UiAsyncState>
  error: ComputedRef<ApiError | null>
  reload: () => Promise<void>
}

export function useReportInsightsPanel(
  options: UseReportInsightsPanelOptions = {},
): UseReportInsightsPanel {
  const account = options.accountId ? null : useAccount()
  const workspace = options.workspaceId ? null : useWorkspace()
  const api = options.api ?? insightApi
  const store = options.store ?? useInsightStore()
  const cacheKey = options.cacheKey ?? 'shell'
  const state = ref<UiAsyncState>('loading')
  const error = ref<ApiError | null>(null)
  let controller: AbortController | null = null
  let generation = 0

  const context = computed(() => ({
    accountId: numericId(options.accountId ? toValue(options.accountId) : account?.accountId.value),
    workspaceId: numericId(
      options.workspaceId ? toValue(options.workspaceId) : workspace?.workspaceId.value,
    ),
  }))
  const insights = computed(() => store.panelCache[cacheKey] ?? [])
  const count = computed(() => store.panelCountCache[cacheKey] ?? insights.value.length)

  async function reload(): Promise<void> {
    const request = ++generation
    controller?.abort()
    const { accountId, workspaceId } = context.value
    if (accountId === undefined || workspaceId === undefined) {
      state.value = 'empty'
      error.value = null
      return
    }

    store.ensureWorkspace(workspaceId)
    const cached = store.panelCache[cacheKey]
    if (cached) {
      state.value = cached.length > 0 ? 'ready' : 'empty'
      error.value = null
      return
    }

    const activeController = new AbortController()
    controller = activeController
    state.value = 'loading'
    error.value = null
    try {
      const page = await api.list(
        accountId,
        workspaceId,
        { limit: options.limit ?? 20, offset: 0 },
        activeController.signal,
      )
      if (request !== generation || activeController.signal.aborted) return
      if (!store.setPanelInsights(workspaceId, cacheKey, page.results, page.count)) return
      state.value = page.results.length > 0 ? 'ready' : 'empty'
    } catch (cause) {
      if (request !== generation || activeController.signal.aborted) return
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind === 'canceled') return
      error.value = normalized
      state.value = 'error'
    }
  }

  watch([context, () => store.panelGeneration], () => void reload(), { immediate: true })
  onScopeDispose(() => {
    generation += 1
    controller?.abort()
  })

  return {
    insights,
    count,
    state: computed(() => state.value),
    error: computed(() => error.value),
    reload,
  }
}

function numericId(value: string | number | undefined): number | undefined {
  if (value === undefined || (typeof value === 'string' && value.trim() === '')) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}
